import {
    Component,
    Input,
    ElementRef,
    AfterViewInit,
    Renderer2,
    OnDestroy,
} from '@angular/core';

@Component({
    selector: 'app-custom-scrollbar',
    templateUrl: './custom-scrollbar.component.html',
    styleUrls: ['./custom-scrollbar.component.scss'],
})
export class CustomScrollbarComponent implements AfterViewInit, OnDestroy {
    @Input() scrollbarClass = '';
    @Input() scrollContainerSelector = '';
    @Input() mobileTrackWidth = '60%';
    @Input() desktopTrackWidth = '40%';

    private scrollContainer!: HTMLElement;
    private scrollTrack!: HTMLElement;
    private scrollbarThumb!: HTMLElement;
    private isContentOverflowing = false;
    private isThumbDragging = false;
    private thumbStartX = 0;
    private scrollLeftStart = 0;

    private readonly FALLBACKWIDTH = 32;

    constructor(private renderer: Renderer2, private elementRef: ElementRef) {}

    ngAfterViewInit(): void {
        this.initializeScrollbar();
        setTimeout(() => this.updateScrollbar(), 0);
        this.enableThumbDrag();
    }

    ngOnDestroy(): void {
        this.scrollContainer.removeEventListener(
            'scroll',
            this.updateThumbPosition
        );
        this.disableThumbDrag();
    }

    private initializeScrollbar(): void {
        const elm = this.elementRef.nativeElement;
        this.scrollContainer = elm.querySelector(this.scrollContainerSelector);
        this.scrollTrack = elm.querySelector('.custom-scrollbar-track');
        this.scrollbarThumb = elm.querySelector('.custom-scrollbar-thumb');

        if (this.scrollContainer) {
            this.renderer.setStyle(
                this.scrollContainer,
                'scrollbar-width',
                'none'
            );
            this.renderer.setStyle(
                this.scrollContainer,
                '-ms-overflow-style',
                'none'
            );
            this.scrollContainer.addEventListener(
                'scroll',
                this.updateThumbPosition
            );
        }
    }

    private updateScrollbar(): void {
        if (!this.scrollContainer || !this.scrollTrack || !this.scrollbarThumb)
            return;

        const containerWidth = this.scrollContainer.clientWidth;
        const contentWidth = this.scrollContainer.scrollWidth;

        this.isContentOverflowing = contentWidth > containerWidth;
        if (!this.isContentOverflowing) {
            this.renderer.addClass(this.scrollTrack, 'hidden');
            return;
        } else this.renderer.removeClass(this.scrollTrack, 'hidden');

        const trackWidth = this.scrollTrack.clientWidth;
        const thumbWidth = Math.max(
            (containerWidth / contentWidth) * trackWidth,
            this.FALLBACKWIDTH
        );

        this.renderer.setStyle(this.scrollbarThumb, 'width', `${thumbWidth}px`);
        this.updateThumbPosition();
    }

    private updateThumbPosition = (): void => {
        if (!this.scrollContainer || !this.scrollTrack || !this.scrollbarThumb)
            return;

        const scrollLeft = this.scrollContainer.scrollLeft;
        const maxScroll =
            this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;
        const maxThumbMove =
            this.scrollTrack.clientWidth - this.scrollbarThumb.offsetWidth;
        const thumbPosition = (scrollLeft / maxScroll) * maxThumbMove;

        this.renderer.setStyle(
            this.scrollbarThumb,
            'transform',
            `translateX(${thumbPosition}px)`
        );
    };

    private enableThumbDrag(): void {
        this.scrollbarThumb.addEventListener(
            'mousedown',
            this.onThumbGrabStart
        );
        window.addEventListener('mouseup', this.onThumbGrabEnd);
        window.addEventListener('mousemove', this.onThumbGrabMove);
    }

    private disableThumbDrag(): void {
        this.scrollbarThumb.removeEventListener(
            'mousedown',
            this.onThumbGrabStart
        );
        window.removeEventListener('mouseup', this.onThumbGrabEnd);
        window.removeEventListener('mousemove', this.onThumbGrabMove);
    }

    private onThumbGrabStart = (event: MouseEvent): void => {
        this.isThumbDragging = true;
        this.thumbStartX = event.clientX;
        this.scrollLeftStart = this.scrollContainer.scrollLeft;
        document.body.style.cursor = 'grabbing';
        event.preventDefault();
    };

    private onThumbGrabMove = (event: MouseEvent): void => {
        if (!this.isThumbDragging) return;

        const distanceInX = event.clientX - this.thumbStartX;

        const scrollRatio =
            (this.scrollContainer.scrollWidth -
                this.scrollContainer.clientWidth) /
            (this.scrollTrack.clientWidth - this.scrollbarThumb.offsetWidth);

        this.scrollContainer.scrollLeft =
            this.scrollLeftStart + distanceInX * scrollRatio;
    };

    private onThumbGrabEnd = (): void => {
        this.isThumbDragging = false;
        document.body.style.cursor = '';
    };
}
